/**
 * Deep link schemes the native apps use to receive the session after SSO.
 * Kept as a closed list - any other scheme is refused.
 * Mirrors MOBILE_APP_SCHEMES in the backend RedirectUrlValidator.
 */
export const MOBILE_APP_SCHEMES = ["sensolusapp://", "alpsalpineconnect://"];

/**
 * True when it is safe to send the browser to target after login.
 *
 * The post-login target arrives in the query string (the SSO state parameter),
 * so it is untrusted: without this check any page linking to
 * /complete_user_info?state=... is an open redirect. Anything that is not a
 * path on this site, a URL on this exact origin, or one of the app deep links
 * is refused. Using the current origin means white-label domains work
 * automatically, since the app is served from the customer's own domain.
 */
export function isSafeRedirectTarget(target: string): boolean {
  if (!target) {
    return false;
  }
  const candidate = target.trim();
  // eslint-disable-next-line no-control-regex
  if (!candidate || /[\u0000-\u001F\u007F]/.test(candidate)) {
    return false;
  }
  const lowerCased = candidate.toLowerCase();
  if (MOBILE_APP_SCHEMES.some((scheme) => lowerCased.startsWith(scheme))) {
    return true;
  }
  if (candidate.startsWith("/")) {
    // "//host" is protocol-relative and browsers read a backslash as a slash,
    // so "/\host" leaves the site too
    return !candidate.startsWith("//") && !candidate.startsWith("/\\");
  }
  try {
    return (
      new URL(candidate, window.location.origin).origin ===
      window.location.origin
    );
  } catch {
    return false;
  }
}

/** Our own domain. Every host under it is ours - see isOwnDomainTarget. */
const OWN_DOMAIN = "sensolus.com";

/**
 * True when target is a page on one of our own hosts.
 *
 * Everything under sensolus.com is ours: the platform, the docs, and the agent
 * apps the micro app manager provisions one DNS label at a time. Two things
 * make this a much smaller allowance than it looks:
 *
 * - The session cookie is already set on ".sensolus.com"
 *   (CookieDomainUtil.getDomain), so the browser sends the token to every one
 *   of these hosts anyway. Sending the user there hands over nothing the host
 *   would not already receive the moment it is visited by any other means.
 * - The one place where our domain does not imply our content is the agent
 *   apps, since an organisation can register one from its own repository. Those
 *   sit behind the JWT proxy, whose check_access refuses anyone outside the
 *   agent's own access list - and refuses everyone when no rule is set - before
 *   the request reaches the agent at all.
 *
 * https only, because the cookie is Secure and a downgrade to http drops it.
 */
export function isOwnDomainTarget(target: string): boolean {
  if (!target) {
    return false;
  }
  const candidate = target.trim();
  // eslint-disable-next-line no-control-regex
  if (!candidate || /[\u0000-\u001F\u007F]/.test(candidate)) {
    return false;
  }
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return false;
  }
  if (parsed.protocol !== "https:") {
    return false;
  }
  // hostname off the parsed URL, so "https://cloud.sensolus.com@evil.com" is
  // read as host evil.com, and a lookalike such as "sensolus.com.evil.com"
  // matches neither branch below
  const host = parsed.hostname.toLowerCase();
  return host === OWN_DOMAIN || host.endsWith(`.${OWN_DOMAIN}`);
}

/**
 * True when it is safe to send the browser to target after a username/password
 * login.
 *
 * Wider than isSafeRedirectTarget by exactly our own hosts, and only usable
 * where nothing of ours is appended to the target: the session already lives in
 * a cookie by the time this runs, so the target receives no credential from the
 * URL. The SSO paths are not like that - the SAML callback appends a single-use
 * loginToken and CompleteUserInfo appends token and csrfToken, and a token in a
 * URL is readable by the page and can be forwarded anywhere - so they keep
 * using isSafeRedirectTarget.
 *
 * An agent app mails deep links into itself wrapped in
 * "/login?redirect_to=<agent url>", and the JWT proxy in front of every agent
 * builds the same URL for its own "Sign in to Sensolus" button, so refusing
 * these targets stranded every agent's sign-in on the platform home page
 * (STIC-15936).
 */
export function isSafeLoginRedirectTarget(target: string): boolean {
  return isSafeRedirectTarget(target) || isOwnDomainTarget(target);
}

/**
 * Builds a login URL with the org parameter for custom branding.
 * Optionally includes a redirect_to parameter.
 */
export function buildLoginUrl(
  orgId?: number | null,
  redirectTo?: string
): string {
  const params = new URLSearchParams();

  if (orgId) {
    params.set("org", String(orgId));
  }
  if (redirectTo) {
    params.set("redirect_to", redirectTo);
  }

  const queryString = params.toString();
  return `/login${queryString ? `?${queryString}` : ""}`;
}
